import { useEffect, useMemo, useRef, useState } from "react"
import { Link, useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom"
import Filter from "pages/purchase/filter"
import nProgress from "nprogress"
import { useAuth } from "contexts/auth-context"
import { Modal, Pagination, Transition } from "components"
import { lang } from "config"
import { Pencil, Plus } from "components/icons/outline"
import { useResultSet } from "repositories/db-repository"
import Delete from "./delete"
import PurchaseExportActions from "./export-actions"
import { getBranchList, getGenericSupplier, getParameter } from "helpers/general-helper"
import toast from "react-hot-toast"

const stripControlChars = (value) =>
  String(value || "")
    .split("")
    .map((char) => {
      const code = char.charCodeAt(0)
      return (code < 32 || (code >= 127 && code <= 159)) ? " " : char
    })
    .join("")

const normalizeText = (value) =>
  stripControlChars(value)
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase()

const parseLegacyDate = (value) => {
  const match = String(value || "").match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/)
  if (!match) return ""

  const day = match[1].padStart(2, "0")
  const month = match[2].padStart(2, "0")
  const rawYear = match[3]
  const year = rawYear.length === 2 ? `20${rawYear}` : rawYear

  return `${year}-${month}-${day}`
}

const parseLegacyDeliveryOrder = (text) => {
  const cleanText = String(text || "").replace(/\r/g, "\n")
  const lines = cleanText.split("\n")
  const normalizedLines = lines.map((line) => stripControlChars(line).trimEnd())

  const getHeaderValue = (label) => {
    const escapedLabel = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    const line = normalizedLines.find((row) => row.includes(label))
    if (!line) return ""
    const match = line.match(new RegExp(`${escapedLabel}\\s*:\\s*(.*?)(?=\\s{2,}\\S[^:]*\\s*:|$)`))
    return (match?.[1] || "").trim()
  }

  const items = []
  let insideItems = false

  for (const row of normalizedLines) {
    const line = row.trim()

    if (/^Kode Barang\s+Jumlah\s+Satuan/i.test(line)) {
      insideItems = true
      continue
    }

    if (!insideItems) continue
    if (!line || /^=+$/.test(line)) continue
    if (/^-+$/.test(line) || /Bersambung/i.test(line) || /^Jumlah\s*=/i.test(line)) {
      insideItems = false
      continue
    }

    const itemMatch = line.match(/^(.+?)\s+([0-9]+(?:[.,][0-9]+)?)\s+([A-Za-z0-9./-]+)$/)
    if (!itemMatch) continue

    const productName = itemMatch[1].replace(/\s+/g, " ").trim()
    const qty = Number(itemMatch[2].replace(",", "."))
    const uom = itemMatch[3].trim()

    if (productName && Number.isFinite(qty) && qty > 0) {
      items.push({ productName, qty, uom })
    }
  }

  const mergedItems = Array.from(
    items
      .reduce((map, item) => {
        const key = `${normalizeText(item.productName)}|${normalizeText(item.uom)}`
        const current = map.get(key)
        map.set(key, current ? { ...current, qty: current.qty + item.qty } : item)
        return map
      }, new Map())
      .values()
  )

  const dateText = getHeaderValue("Tanggal")

  return {
    legacyNoBukti: getHeaderValue("No.Bukti"),
    legacyDoNo: getHeaderValue("No.DO"),
    customerName: getHeaderValue("Customer"),
    address: getHeaderValue("Alamat"),
    poDate: parseLegacyDate(dateText),
    items: mergedItems,
  }
}

const pickProductId = (row) => row?.product_unit_id ?? row?.id ?? ""

const pickCostPrice = (row) => {
  const candidates = [
    row?.price,
    row?.pcs_price,
    row?.maximum_retail_price,
    row?.purchase_price,
    row?.buy_price,
    row?.cost_price,
    row?.hpp,
  ]

  for (const candidate of candidates) {
    const value = Number(candidate)
    if (Number.isFinite(value) && value > 0) return value
  }

  return 0
}

function ImportLegacyDO({ branchSelection, currentBranch, supplierSelection, userName, onSuccess }) {
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [fileName, setFileName] = useState("")
  const [parsed, setParsed] = useState(null)
  const [outletId, setOutletId] = useState(currentBranch?.id ?? "")
  const [supplierId, setSupplierId] = useState("")
  const [errors, setErrors] = useState([])
  const [warnings, setWarnings] = useState([])
  const [submitting, setSubmitting] = useState(false)

  const reset = () => {
    setFileName("")
    setParsed(null)
    setErrors([])
    setWarnings([])
    setSubmitting(false)
    setOutletId(currentBranch?.id ?? "")
    setSupplierId("")
  }

  const closeModal = () => {
    if (submitting) return
    setIsOpen(false)
    reset()
  }

  const readFileAsText = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result || "")
      reader.onerror = () => reject(reader.error || new Error("Gagal membaca file"))
      reader.readAsText(file)
    })

  const onFileChange = async (event) => {
    const file = event.target.files?.[0]
    setErrors([])
    setWarnings([])
    setParsed(null)
    setFileName(file?.name || "")

    if (!file) return

    try {
      const text = await readFileAsText(file)
      const result = parseLegacyDeliveryOrder(text)

      if (!result.items.length) {
        setErrors(["Item DO tidak terbaca. Pastikan file berisi tabel Kode Barang, Jumlah, Satuan."])
      }

      setParsed(result)
    } catch (error) {
      setErrors([error?.message || "Gagal membaca file DO"])
    }
  }

  const resolveLegacyProductMappings = async (productNames) => {
    const base = (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")
    const names = Array.from(
      (productNames || [])
        .reduce((map, name) => {
          const key = normalizeText(name)
          if (key && !map.has(key)) map.set(key, name)
          return map
        }, new Map())
        .values()
    )

    if (!base || !names.length) return new Map()

    const response = await fetch(`${base}/legacy-product-unit-mappings/resolve`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        names,
        outlet_id: outletId || getParameter("REMAX_HQ_OUTLET_ID") || currentBranch?.id || "",
        active_date: parsed?.poDate || new Date().toISOString().slice(0, 10),
      }),
    })

    const json = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(json?.message || "Gagal membaca mapping produk lama")

    const rows = Array.isArray(json?.data) ? json.data : []

    return rows.reduce((map, row) => {
      const key = normalizeText(row?.legacy_name || row?.normalized_legacy_product_name)
      if (key && row?.mapped && pickProductId(row)) map.set(key, row)
      return map
    }, new Map())
  }

  const extractRows = (json) => {
    if (Array.isArray(json?.data)) return json.data
    if (Array.isArray(json?.data?.data)) return json.data.data
    if (Array.isArray(json?.data?.data?.data)) return json.data.data.data
    return []
  }

  const fetchGoodsReceiptForPo = async (poId) => {
    const base = (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")
    const params = new URLSearchParams({
      po_id: poId,
      status: "DRAFT",
      paginate: "false",
      per_page: "1",
    })

    const response = await fetch(`${base}/goods-receipts?${params.toString()}`)
    const json = await response.json().catch(() => ({}))

    if (!response.ok) throw new Error(json?.message || "Gagal mengambil Goods Receipt")

    return extractRows(json)[0] || null
  }

  const updateGoodsReceiptHeader = async (receiptId) => {
    const base = (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")
    const response = await fetch(`${base}/goods-receipts/${receiptId}/header`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        receipt_date: parsed.poDate || new Date().toISOString().slice(0, 10),
        supplier_do_number: parsed.legacyDoNo || null,
        remarks: [
          parsed.legacyNoBukti ? `Import DO lama No.Bukti: ${parsed.legacyNoBukti}` : null,
          parsed.customerName ? `Customer: ${parsed.customerName}` : null,
          parsed.address ? `Alamat: ${parsed.address}` : null,
        ].filter(Boolean).join("\n") || null,
      }),
    })

    const json = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(json?.message || "Gagal update header Goods Receipt")
    return json
  }

  const fetchSatuanMap = async () => {
    const base = (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")
    if (!base) return new Map()

    const response = await fetch(`${base}/satuan?paginate=false`)
    const json = await response.json().catch(() => ({}))
    if (!response.ok) return new Map()

    const rows = Array.isArray(json?.data) ? json.data : (Array.isArray(json?.data?.data) ? json.data.data : [])

    const map = new Map()
    for (const row of rows) {
      const key = normalizeText(row?.name)
      const qty = Number(row?.qty_per_unit)
      if (key && Number.isFinite(qty) && qty > 0) {
        map.set(key, qty)
      }
    }
    return map
  }

  const buildItems = async () => {
    const notFound = []
    const matchWarnings = []
    const warningSet = new Set()
    const resolved = []

    const addWarning = (message) => {
      if (!message || warningSet.has(message)) return
      warningSet.add(message)
      matchWarnings.push(message)
    }

    const [mappingMap, satuanMap] = await Promise.all([
      resolveLegacyProductMappings(parsed.items.map((item) => item.productName)),
      fetchSatuanMap(),
    ])

    for (const item of parsed.items) {
      const mapping = mappingMap.get(normalizeText(item.productName))

      if (!mapping || !pickProductId(mapping)) {
        notFound.push(item.productName)
        continue
      }

      if (!pickCostPrice(mapping)) {
        addWarning(`Harga active belum ada: ${item.productName}`)
      }

      // Konversi qty legacy uom (mis. BOX/LUSIN) ke pcs berdasarkan master Satuan.
      // Pcs di master harus qty_per_unit = 1. Jika uom tidak dikenal, fallback 1:1 + warning.
      const uomKey = normalizeText(item.uom)
      const factor = satuanMap.get(uomKey)
      if (factor === undefined) {
        addWarning(`Satuan "${item.uom}" tidak ada di master — dianggap 1:1 ke pcs.`)
      }
      const conversion = Number.isFinite(factor) && factor > 0 ? factor : 1
      const qtyInPcs = Number(item.qty) * conversion

      resolved.push({
        product_unit_id: String(pickProductId(mapping)),
        uom: 'PCS',
        qty_ordered: qtyInPcs,
        qty_received: 0,
        cost_price: pickCostPrice(mapping),
        sell_price_ref: Number(mapping?.sell_price_ref ?? 0),
      })
    }

    const merged = Array.from(
      resolved
        .reduce((map, item) => {
          const current = map.get(item.product_unit_id)
          map.set(
            item.product_unit_id,
            current
              ? { ...current, qty_ordered: Number(current.qty_ordered) + Number(item.qty_ordered) }
              : item
          )
          return map
        }, new Map())
        .values()
    )

    return { items: merged, notFound, matchWarnings }
  }

  const submitImport = async (event) => {
    event.preventDefault()
    setErrors([])
    setWarnings([])

    if (!outletId) {
      toast.error("Pilih outlet terlebih dahulu")
      return
    }

    if (!supplierId) {
      toast.error("Pilih supplier terlebih dahulu")
      return
    }

    if (!parsed?.items?.length) {
      toast.error("Upload file DO terlebih dahulu")
      return
    }

    try {
      setSubmitting(true)
      nProgress.start()

      const { items, notFound, matchWarnings } = await buildItems()

      if (notFound.length) {
        setErrors(notFound.map((name) => `Mapping produk lama belum dibuat: ${name}`))
        if (matchWarnings.length) setWarnings(matchWarnings)
        return
      }

      const base = (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")
      const response = await fetch(`${base}/purchase-orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          outlet_id: Number(outletId),
          supplier_id: supplierId,
          po_date: parsed.poDate || new Date().toISOString().slice(0, 10),
          po_type: "REGULAR",
          status: "DRAFT",
          remarks: [
            parsed.legacyDoNo ? `Import DO lama: ${parsed.legacyDoNo}` : null,
            parsed.legacyNoBukti ? `No.Bukti: ${parsed.legacyNoBukti}` : null,
            parsed.customerName ? `Customer: ${parsed.customerName}` : null,
            parsed.address ? `Alamat: ${parsed.address}` : null,
          ].filter(Boolean).join("\n"),
          createdBy: userName,
          items,
        }),
      })

      const json = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(json?.message || "Gagal membuat PO dari DO")

      const poId = json?.data?.id || json?.id
      if (!poId) throw new Error("PO berhasil dibuat, tapi ID PO tidak ditemukan dari response")

      const approveResponse = await fetch(`${base}/purchase-orders/${poId}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ approvedBy: userName }),
      })
      const approveJson = await approveResponse.json().catch(() => ({}))
      if (!approveResponse.ok) throw new Error(approveJson?.message || "PO dibuat, tapi gagal approve")

      const receipt = await fetchGoodsReceiptForPo(poId)
      if (!receipt?.id) throw new Error("PO sudah approved, tapi Goods Receipt draft tidak ditemukan")

      try {
        await updateGoodsReceiptHeader(receipt.id)
      } catch (headerError) {
        toast.error(headerError?.message || "Goods Receipt dibuat, tapi header belum ter-update")
      }

      if (matchWarnings.length) setWarnings(matchWarnings)
      toast.success("Import DO berhasil. PO approved dan Goods Receipt draft dibuat.")
      setIsOpen(false)
      reset()
      onSuccess?.()
      navigate(`/goods-receipt/${receipt.id}/edit`)
    } catch (error) {
      setErrors([error?.message || "Gagal import DO"])
      toast.error(error?.message || "Gagal import DO")
    } finally {
      nProgress.done()
      setSubmitting(false)
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="inline-flex items-center px-4 py-3 space-x-2 text-xs transition border border-neutral-200 bg-neutral-50 rounded-xl active:hover:scale-90"
      >
        <span>Import Surat Jalan</span>
      </button>

      <Modal
        isOpen={isOpen}
        onClose={closeModal}
        title="Import Surat Jalan Lama"
        content={(
          <form onSubmit={submitImport} className="mt-6 space-y-5 text-xs">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="block font-medium text-neutral-700">Outlet</label>
                <select
                  value={outletId || ""}
                  onChange={(event) => setOutletId(event.target.value)}
                  className="block w-full px-3 py-3 text-xs border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
                >
                  <option value="">Select Outlet</option>
                  {(branchSelection || []).map((branch) => (
                    <option key={branch.id} value={branch.id}>{branch.name}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className="block font-medium text-neutral-700">Supplier</label>
                <select
                  value={supplierId || ""}
                  onChange={(event) => setSupplierId(event.target.value)}
                  className="block w-full px-3 py-3 text-xs border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
                >
                  <option value="">Select Supplier</option>
                  {(supplierSelection || []).map((supplier) => (
                    <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-1">
              <label className="block font-medium text-neutral-700">File DO</label>
              <input
                type="file"
                accept=".prn,.PRN,.txt"
                onChange={onFileChange}
                className="block w-full text-xs text-neutral-700 border border-neutral-200 rounded-xl cursor-pointer file:mr-3 file:px-3 file:py-2 file:text-xs file:font-medium file:border-0 file:rounded-xl file:bg-neutral-800 file:text-white hover:file:bg-neutral-900"
              />
              {fileName && <p className="text-[11px] text-neutral-500">Selected file: <span className="font-medium">{fileName}</span></p>}
            </div>

            {parsed && (
              <div className="overflow-hidden border border-neutral-200 rounded-xl">
                <div className="grid grid-cols-2 gap-2 p-3 bg-neutral-50 text-[11px] text-neutral-700">
                  <div>No.Bukti: <b>{parsed.legacyNoBukti || "-"}</b></div>
                  <div>No.DO: <b>{parsed.legacyDoNo || "-"}</b></div>
                  <div>Tanggal: <b>{parsed.poDate || "-"}</b></div>
                  <div>Total Qty: <b>{parsed.items.reduce((sum, item) => sum + item.qty, 0)}</b></div>
                  <div className="col-span-2">Customer: <b>{parsed.customerName || "-"}</b></div>
                </div>
                <div className="max-h-56 overflow-auto">
                  <table className="min-w-full divide-y divide-neutral-200">
                    <thead className="bg-white">
                      <tr>
                        <th className="px-3 py-2 text-[11px] font-medium text-left text-neutral-500 uppercase">Kode Barang</th>
                        <th className="px-3 py-2 text-[11px] font-medium text-right text-neutral-500 uppercase">Qty</th>
                        <th className="px-3 py-2 text-[11px] font-medium text-left text-neutral-500 uppercase">Satuan</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                      {parsed.items.slice(0, 80).map((item) => (
                        <tr key={`${item.productName}-${item.uom}`}>
                          <td className="px-3 py-2 text-[11px] text-neutral-800">{item.productName}</td>
                          <td className="px-3 py-2 text-[11px] text-right text-neutral-800">{item.qty}</td>
                          <td className="px-3 py-2 text-[11px] text-neutral-800">{item.uom}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {warnings.length > 0 && (
              <div className="p-3 text-[11px] border rounded-xl border-amber-200 bg-amber-50 text-amber-800 max-h-32 overflow-auto">
                <div className="mb-1 font-semibold">Match tidak exact:</div>
                <ul className="pl-4 space-y-1 list-disc">
                  {warnings.map((message, index) => <li key={index}>{message}</li>)}
                </ul>
              </div>
            )}

            {errors.length > 0 && (
              <div className="p-3 text-[11px] border rounded-xl border-red-200 bg-red-50 text-red-700 max-h-40 overflow-auto">
                <div className="mb-1 font-semibold">Error:</div>
                <ul className="pl-4 space-y-1 list-disc">
                  {errors.map((message, index) => <li key={index}>{message}</li>)}
                </ul>
              </div>
            )}

            <div className="flex items-center gap-2">
              <button
                type="submit"
                disabled={submitting}
                className="px-5 py-3 text-xs font-semibold text-white bg-neutral-800 rounded-xl hover:bg-neutral-900 disabled:opacity-60"
              >
                {submitting ? "Memproses..." : "Import dan Buka GR"}
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={closeModal}
                className="px-5 py-3 text-xs font-semibold border border-neutral-200 rounded-xl text-neutral-700 hover:bg-neutral-50 disabled:opacity-60"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      />
    </>
  )
}

export default function List() {
  const { can, currentBranch, user } = useAuth()
  const { title, transition, setTransition } = useOutletContext()

  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()

  const page = searchParams.get("page")
  const status = searchParams.get("status")
  const searchParam = searchParams.get("search") ?? ""

  // Local state untuk search (debounced biar gak spam history)
  const [search, setSearch] = useState(searchParam)
  const searchInput = useRef(null)
  const searchDebounceRef = useRef(null)

  useEffect(() => setSearch(searchParam), [searchParam])

  const branchSelection = getBranchList()
  const supplierSelection = getGenericSupplier()
  const branchIdList = useMemo(() => {
    if (!branchSelection) return []
    return branchSelection.map((x) => x.id).filter(Boolean)
  }, [branchSelection])

  const { data: result_set, isLoading, mutate } = useResultSet(
    `${process.env.REACT_APP_INVENTORY_API_URL}`,
    `purchase_transaction`,
    {
      outlet_id: currentBranch?.id,
      branch_list: JSON.stringify(branchIdList),
      page,
      record_per_page: 5,
      search: searchParam || undefined,
      latest_flow_detail_id: status,
    }
  )

  // const { data: list_flow } = useResultSet(
  //   `${process.env.REACT_APP_INVENTORY_API_URL}`,
  //   `list_flow`,
  //   {}
  // )

  useEffect(() => {
    if (transition?.reverse) setTransition({ type: "slide", reverse: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (searchInput.current) searchInput.current.focus()
  }, [])

  useEffect(() => {
    if (isLoading) nProgress.start()
    else nProgress.done()
  }, [isLoading])

  const patchSearchParams = (patch, opts = {}) => {
    const next = new URLSearchParams(searchParams)

    Object.entries(patch || {}).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "") next.delete(k)
      else next.set(k, String(v))
    })

    // reset page ketika filter berubah
    if (patch && (Object.prototype.hasOwnProperty.call(patch, "search") || Object.prototype.hasOwnProperty.call(patch, "status"))) {
      next.delete("page")
    }

    setSearchParams(next, opts)
  }

  const updateFilter = (value) => patchSearchParams(value)
  const removeFilter = () => setSearchParams({})

  const onSearchChange = (val) => {
    setSearch(val)
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    searchDebounceRef.current = setTimeout(() => {
      patchSearchParams({ search: val })
    }, 350)
  }

  const generatePaginationParam = () => {
    let tmp = ""
    if (status) tmp += `&status=${status}`
    if (searchParams.has("search")) tmp += `&search=${searchParams.get("search")}`
    return tmp
  }

  // normalize list dari response baru
  const list = useMemo(() => {
    if (Array.isArray(result_set?.data?.data)) return result_set.data?.data
    return []
  }, [result_set])

  // untuk Pagination component (kalau komponen kamu masih expect fields ini)
  const paginationLinks = result_set?.data?.links
  const total = result_set?.data?.total
  const from = result_set?.data?.from
  const to = result_set?.data?.to

  const statusBadge = (st) => {
    const s = String(st || "").toUpperCase()
    const base = "inline-flex items-center px-2 py-1 rounded-lg text-[11px] font-medium"
    if (s === "APPROVED") return <span className={`${base} bg-blue-50 text-blue-700`}>{st}</span>
    if (s === "RECEIVED") return <span className={`${base} bg-green-50 text-green-700`}>{st}</span>
    if (s === "CLOSED") return <span className={`${base} bg-green-50 text-emerald-700`}>{st}</span>
    if (s === "PARTIAL_RECEIVED") return <span className={`${base} bg-amber-50 text-amber-700`}>{st}</span>
    if (s === "DRAFT") return <span className={`${base} bg-neutral-100 text-neutral-700`}>{st}</span>
    if (s === "CANCELLED" || s === "REJECTED") return <span className={`${base} bg-red-50 text-red-700`}>{st}</span>
    return <span className={`${base} bg-neutral-100 text-neutral-700`}>{st || "-"}</span>
  }

  const formatDateTime = (value) => {
    if (!value) return "-"

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value

    return new Intl.DateTimeFormat("id-ID", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(date)
  }

  const postJson = async (path, body = null) => {
    const url = `${process.env.REACT_APP_INVENTORY_API_URL.replace(/\/$/, "")}${path}`
    const resp = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: body ? JSON.stringify(body) : null,
      // credentials: "include", // kalau auth cookie-based
    })

    const json = await resp.json().catch(() => ({}))
    if (!resp.ok) {
      const msg = json?.message || "Request gagal"
      const err = json?.error ? `: ${json.error}` : ""
      throw new Error(msg + err)
    }
    return json
  }

  const [actingId, setActingId] = useState(null)

  const onApprove = async (po) => {
    if (!po?.id) return
    if (!window.confirm(`Approve PO ${po.po_number || ""}?`)) return

    try {
      setActingId(po.id)
      await postJson(`/purchase-orders/${po.id}/approve`)
      await mutate()
    } catch (e) {
      alert(e.message || "Gagal approve")
    } finally {
      setActingId(null)
    }
  }

  const onCancel = async (po) => {
    if (!po?.id) return
    const reason = window.prompt(`Cancel PO ${po.po_number || ""}\nAlasan (optional):`, "")
    if (reason === null) return // user cancel prompt

    try {
      setActingId(po.id)
      await postJson(`/purchase-orders/${po.id}/cancel`, reason ? { reason } : null)
      await mutate()
    } catch (e) {
      alert(e.message || "Gagal cancel")
    } finally {
      setActingId(null)
    }
  }

  const onClose = async (po) => {
    if (!po?.id) return

    if (!window.confirm(`Close PO ${po.po_number || ""}?`)) return

    try {
      setActingId(po.id)

      await postJson(`/purchase-orders/${po.id}/close`)

      await mutate()
    } catch (e) {
      alert(e.message || "Gagal close PO")
    } finally {
      setActingId(null)
    }
  }

  const canClose = (po) => {
    const st = String(po?.status || "").toUpperCase()
    return ["PARTIAL_RECEIVED", "RECEIVED"].includes(st)
  }

  const canApprove = (po) => {
    const st = String(po?.status || "").toUpperCase()
    return st === "DRAFT"
  }

  const canPrint = (po) => {
    const st = String(po?.status || "").toUpperCase()
    return st === "APPROVED"
  }

  const canCancel = (po) => {
    const st = String(po?.status || "").toUpperCase()
    return !["CANCELLED", "RECEIVED", "PARTIAL_RECEIVED","REJECTED"].includes(st)
  }

  return (
    <Transition type="fade">
      <div className="pb-4 space-y-6 lg:pb-8">
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center sm:space-x-2">
            <div className="relative hidden sm:block">
              <div className="absolute inset-y-0 flex items-center pl-3 pointer-events-none">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" width={24} height={24} viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                  <circle cx={10} cy={10} r={7}></circle>
                  <line x1={21} y1={21} x2={15} y2={15}></line>
                </svg>
              </div>

              <input
                ref={searchInput}
                onChange={(e) => onSearchChange(e.target.value)}
                value={search}
                type="text"
                placeholder={`${lang.search} Purchase Order`}
                autoComplete="off"
                className="w-64 py-3 pl-8 pr-4 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
              />
            </div>

            {/* <Filter
              // list_flow={list_flow}
              onSubmit={updateFilter}
              onRemove={removeFilter}
              data={Object.fromEntries(Object.entries({ status }).filter(([_, v]) => v != null))}
            /> */}

            <select
              value={status || ""}
              onChange={(e) => patchSearchParams({ status: e.target.value })}
              className="hidden sm:block py-3 px-3 text-xs transition border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
            >
              <option value="">All Status</option>
              <option value="DRAFT">DRAFT</option>
              <option value="SUBMITTED">SUBMITTED</option>
              <option value="APPROVED">APPROVED</option>
              <option value="REJECTED">REJECTED</option>
              <option value="RECEIVED">RECEIVED</option>
              <option value="PARTIAL_RECEIVED">PARTIAL_RECEIVED</option>
              <option value="CLOSED">CLOSED</option>
              <option value="CANCELLED">CANCELLED</option>
            </select>
          </div>

          <div className="flex items-center space-x-2">
            {can("purchase-order.create") && (
              <ImportLegacyDO
                branchSelection={branchSelection}
                currentBranch={currentBranch}
                supplierSelection={supplierSelection}
                userName={user?.name}
                onSuccess={mutate}
              />
            )}

            {can("purchase-order.create") && (
              <div>
                <Link
                  to="create"
                  onClick={() => setTransition({ type: "slide", reverse: true })}
                  state={{ back: title, from: location?.pathname, transition: { type: "slide" } }}
                  className="inline-flex items-center px-4 py-3 space-x-2 text-white transition bg-neutral-800 rounded-xl active:hover:scale-90"
                >
                  <Plus className="w-4 h-4" stroke={1.5} />
                  <span>{lang.create}</span>
                </Link>
              </div>
            )}
          </div>
        </div>

        <div className="overflow-x-auto border border-neutral-200 rounded-xl">
          <table className="min-w-full overflow-x-auto divide-y divide-neutral-200">
            <thead className="bg-neutral-50 rounded-t-3xl">
              <tr>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">PO No</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Type</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Supplier</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Date</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Status</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Items</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Qty</th>
                <th className="relative px-3 py-3"><span className="sr-only">{lang.action}</span></th>
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-neutral-200">
              {isLoading && (
                <tr className="text-center">
                  <td colSpan={8} className="px-3 py-6 text-xs text-neutral-500 whitespace-nowrap">
                    {lang.loading_data}
                  </td>
                </tr>
              )}

              {!isLoading && list.length === 0 && !searchParam && (
                <tr className="text-center">
                  <td colSpan={8} className="px-3 py-6 text-xs text-neutral-500 whitespace-nowrap">
                    {lang.no_data}
                  </td>
                </tr>
              )}

              {!isLoading && list.length === 0 && searchParam && (
                <tr className="text-center">
                  <td colSpan={8} className="px-3 py-6 text-xs text-neutral-500 whitespace-nowrap">
                    {lang.no_result}
                  </td>
                </tr>
              )}

              {!isLoading && list.map((po) => (
                <tr key={po.id}>
                  <td className="px-3 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">
                    {po.po_number ?? "-"}
                  </td>
                  <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                    {po.po_type ?? "-"}
                  </td>
                  <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                    {po.supplier_name ?? po.supplier_name_snapshot ?? po.supplier_id ?? "-"}
                  </td>
                  <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                    {formatDateTime(po.po_date) ?? "-"}
                  </td>
                  <td className="px-3 py-4 text-xs whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      {statusBadge(po.status)}
                      {String(po.status || "").toUpperCase() === "REJECTED" && po.remarks ? (
                        <span
                          className="text-[11px] text-red-600 whitespace-normal max-w-[220px]"
                          title={po.remarks}
                        >
                          {po.remarks}
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                    {po.items_count ?? "-"}
                  </td>
                  <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                    {po.status === "CLOSED" ? parseInt(po.qty_received_total) : parseInt(po.qty_total) ?? "-"}
                  </td>

                  <td className="px-3 py-4 text-xs font-medium text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-2">
                      {(can("purchase-order.update") || can("purchase-order.approve")) && (
                        <Link
                          to={`${po.id}/edit`}
                          state={{ back: title, from: location?.pathname, transition: "slide" }}
                          className="inline-flex items-center p-1 text-white transition rounded-full bg-neutral-800 active:hover:scale-90"
                        >
                          <Pencil className="w-6 h-6" />
                        </Link>
                      )}

                      {/* {can("purchase-order.approve") && canApprove(po) && (
                        <button
                          type="button"
                          disabled={actingId === po.id}
                          onClick={() => onApprove(po)}
                          className="px-3 py-2 text-[11px] font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-500 disabled:opacity-60"
                        >
                          {actingId === po.id ? "..." : "Approve"}
                        </button>
                      )} */}

                      {can("purchase-order.close") && canClose(po) && (
                        <button
                          type="button"
                          disabled={actingId === po.id}
                          onClick={() => onClose(po)}
                          className="px-3 py-2 text-[11px] font-semibold text-white bg-neutral-700 rounded-lg hover:bg-neutral-600 disabled:opacity-60"
                        >
                          {actingId === po.id ? "..." : "Close"}
                        </button>
                      )}

                      {canPrint(po) && (
                        <PurchaseExportActions poId={po.id} compact />
                      )}


                      {/* {can("purchase-order.cancel") && canCancel(po) && (
                        <button
                          type="button"
                          disabled={actingId === po.id}
                          onClick={() => onCancel(po)}
                          className="px-3 py-2 text-[11px] font-semibold text-white bg-red-600 rounded-lg hover:bg-red-500 disabled:opacity-60"
                        >
                          {actingId === po.id ? "..." : "Cancel"}
                        </button>
                      )} */}

                      {/* {can("purchase-order.delete") && (
                        <Delete data={po} onSuccess={mutate} />
                      )} */}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination
          params={generatePaginationParam()}
          links={paginationLinks}
          from={from}
          to={to}
          total={total}
        />
      </div>
    </Transition>
  )
}
